import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { botsApi } from "@/lib/api/bots";
import { BotCreate, BotUpdate } from "@/types/bot";
import { toast } from "sonner";

export const botKeys = {
  all: ["bots"] as const,
  detail: (id: string) => ["bots", id] as const,
  models: ["bots", "models"] as const,
};

export function useBots() {
  return useQuery({
    queryKey: botKeys.all,
    queryFn: botsApi.getAll,
  });
}

export function useModels() {
  return useQuery({
    queryKey: botKeys.models,
    queryFn: botsApi.getModels,
  });
}

export function useBot(botId: string) {
  return useQuery({
    queryKey: botKeys.detail(botId),
    queryFn: () => botsApi.getById(botId),
    enabled: !!botId,
  });
}

export function useCreateBot() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: BotCreate) => botsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: botKeys.all });
      toast.success("Bot created successfully.");
    },
    onError: (error: unknown) => {
      const message =
        (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        "Failed to create bot.";
      toast.error(message);
      console.error(error);
    },
  });
}

export function useUpdateBot() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ botId, data }: { botId: string; data: BotUpdate }) => 
      botsApi.update(botId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: botKeys.all });
      queryClient.invalidateQueries({ queryKey: botKeys.detail(variables.botId) });
      toast.success("Bot updated successfully.");
    },
    onError: (error: unknown) => {
      const message =
        (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        "Failed to update bot.";
      toast.error(message);
      console.error(error);
    },
  });
}

export function useDeleteBot() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (botId: string) => botsApi.delete(botId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: botKeys.all });
      toast.success("Bot deleted successfully.");
    },
    onError: (error) => {
      toast.error("Failed to delete bot.");
      console.error(error);
    },
  });
}
